'use client';

import { useCallback } from 'react';
import {
	BN,
	SwapMode,
	UnifiedQuoteResponse,
	SpotBalanceType,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	MarketId,
	matchEnum,
	JLP_POOL_ID,
} from '@velocity-exchange/common';
import { Adapter } from '@solana/wallet-adapter-base';

import useDriftClient from 'src/hooks/useDriftClient';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useTxDataStore from 'src/stores/useTxDataStore';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { notify, NewNotificationProps } from 'src/utils/notifications';
import TransactionErrorHandler, {
	AnchorError,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { buildSwapTxWithRetryLogic } from './helpers/buildSwapTxWithRetryLogic';
import { JLP_POOL_JLP_MARKET_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { AccountData } from 'src/stores/useDriftAccountsStore';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import invariant from 'tiny-invariant';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

interface SwapProps {
	userKey: string;
	amount: BN;
	fromMarketIndex: number;
	toMarketIndex: number;
	swapMode: SwapMode;
	quote: UnifiedQuoteResponse;
	onlyDirectRoutes?: boolean;
	slippageBps?: number;
	suppressToasts?: boolean;
}

/**
 * Hook for executing spot swaps with retry logic.
 */
export const useSwap = () => {
	const driftClient = useDriftClient();
	const swapClient = useDriftStore((s) => s.swapClient);
	const wallet = useDriftStore((s) => s.wallet?.current?.adapter);
	const getAccountsState = useDriftAccountsStore((s) => s.get);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const addTxErrorToTxDataStore = useTxDataStore((s) => s.addNewTxError);
	const getTxParams = useGetTxParams();

	const swap = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				_isAutoSigned: boolean,
				toastId: string,
				swapProps: SwapProps
			): Promise<boolean> => {
				const {
					userKey,
					amount,
					fromMarketIndex,
					toMarketIndex,
					swapMode,
					quote,
					onlyDirectRoutes = false,
					slippageBps = 100,
					suppressToasts = false,
				} = swapProps;

				if (!quote) {
					notify({
						type: 'error',
						message: 'Swap Failed',
						description: 'Could not fetch swap quote. Please try again',
						id: toastId,
					});

					return false;
				}

				invariant(driftClient, 'driftClient is required');
				invariant(swapClient, 'swapClient is required');

				const accountsState = getAccountsState();
				const accountData = accountsState.accounts[userKey] as AccountData;

				// block the swap if it's opening a borrow on the deposit asset in an amplify account
				if (isAmplifyAccountName(accountData.name)) {
					// add to this when more pools get added
					if (
						accountData.poolId === JLP_POOL_ID &&
						fromMarketIndex === JLP_POOL_JLP_MARKET_INDEX
					) {
						const userDepositAssetBalance = accountData.spotBalances.find(
							(bal) =>
								bal.asset.marketIndex === JLP_POOL_JLP_MARKET_INDEX &&
								matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
						)?.balance;

						if (amount.gt(userDepositAssetBalance?.val ?? ZERO)) {
							notify({
								type: 'error',
								message: 'Invalid Amplify Position',
								id: toastId,
							});
							return false;
						}
					}
				}

				driftClient?.switchActiveUser(
					accountData.userId,
					accountData.authority
				);

				const inMarketConfig = UIMarket.createSpotMarket(fromMarketIndex);
				const outMarketConfig = UIMarket.createSpotMarket(toMarketIndex);

				try {
					const toastProps: NewNotificationProps = {
						id: toastId,
						type: 'awaiting_wallet_confirmation',
						showUntilCancelled: true,
						timeoutIfProlongedTxn: true,
						updatePrevious: true,
						overrideBlockSubsequentToasts: true,
						blockSubsequentToasts: false,
						action: undefined,
					};

					if (!suppressToasts) {
						notify(toastProps);
					}

					const oraclePrice = getOraclePriceForMarket(
						MarketId.createSpotMarket(toMarketIndex)
					);

					const usdEstimate = Number(quote.outAmount) * oraclePrice.toNum();

					const accountPubKey = accountData.pubKey;

					const slippage = slippageBps / 100;

					const { ixs, lookupTables, success } =
						await buildSwapTxWithRetryLogic({
							accountPubKey,
							inputMarketConfig: inMarketConfig.market as any,
							outputMarketConfig: outMarketConfig.market as any,
							amount,
							swapMode,
							slippage,
							usdEstimate,
							maxRetries: 5,
							additionalIxs: [],
							onlyDirectRoutes,
							driftClient,
							swapClient,
							getTxParams,
						});

					if (success) {
						const txParams = getTxParams({
							computeUnits: MAX_COMPUTE_UNITS,
							useSimulatedComputeUnits: false,
						});

						await signerWallet.signAndSendTransaction(
							ixs,
							txParams,
							{
								onSendTransactionStart: () => {
									if (!suppressToasts) {
										notify({
											...toastProps,
											type: 'info',
											message: 'Sending swap.',
										});
									}
								},
							},
							lookupTables
						);

						notify({
							type: 'success',
							message: 'Swap successful.',
							id: toastId,
							updatePrevious: true,
						});

						return true;
					} else {
						// TODO: find a way to exact error message from buildSwapTxWithRetryLogic
						throw new Error('Swap simulation failed');
					}
				} catch (err: any) {
					if (err instanceof FeePayerError) {
						throw err;
					}

					addTxErrorToTxDataStore({
						error: err?.message ?? 'Unknown swap error',
						type: 'swap',
						ts: Date.now(),
					});

					const customErrorHandler = (err: AnchorError) => {
						// # Transaction size too large
						if (
							(err?.message?.match(/\(max: encoded\/raw \d+\/\d+\)/) && true) ||
							(err?.message?.match(/Transaction locked too many accounts/) &&
								true)
						) {
							notify({
								type: 'error',
								message: 'Transaction error.',
								description: `Some swaps on Drift will fail if the swap route is too complex. Please try again using the "Use direct route" option.`,
								id: toastId,
								updatePrevious: true,
								subDescription: undefined,
							});

							return true;
						}

						return false;
					};

					TransactionErrorHandler.handleError({
						error: err,
						toastId,
						customErrorHandler,
						wallet: wallet as Adapter,
					});

					return false;
				}
			},
			[
				driftClient,
				swapClient,
				getAccountsState,
				getOraclePriceForMarket,
				addTxErrorToTxDataStore,
				getTxParams,
				wallet,
			]
		),
		true // NOTE: we disable gas sponsorship for now, because swaps involve other programs (e.g. Jupiter, Titan) that aren't whitelisted yet
	);

	return { swap };
};
